/**
 * PQP HD Wallet - Hierarchical Deterministic Wallet with Post-Quantum Protection
 * Derives all keys from a single seed phrase, compatible with BIP39/BIP32/BIP44
 */

import { secp256k1 } from '@noble/curves/secp256k1'
import { sha256 } from '@noble/hashes/sha256'
import { ripemd160 } from '@noble/hashes/ripemd160'
import { hmac } from '@noble/hashes/hmac'
import { sha512 as sha512Hash } from '@noble/hashes/sha512'
import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from '@scure/bip39'
import { wordlist } from '@scure/bip39/wordlists/english'
import { bech32 } from 'bech32'
import { base58check } from '@scure/base'
import { QuantumKeyManager, type QuantumKeyPair } from './quantum-keys'
import { WebCrypto } from './web-crypto'
import { DilithiumRKSWrapper, getDilithiumRKS } from './dilithium-rks-wrapper'
import { FEATURES } from '@/types/settings'
import { useWalletAddresses } from '@/lib/store/wallet-addresses'
import { logger as importedLogger } from '@/lib/utils/logger'

// Fallback-protected logger (Low severity fix)
const logger = {
  warn: importedLogger?.warn || console.warn.bind(console),
  error: importedLogger?.error || console.error.bind(console),
  info: importedLogger?.info || console.info.bind(console)
}

// BIP44 path: m/purpose'/coin_type'/account'/change/address_index
// For quantum wallets, we use purpose 3077' (0xC05 = 3077)
const QUANTUM_PURPOSE = 3077
const BITCOIN_COIN_TYPE = 0
const TESTNET_COIN_TYPE = 1

interface HDNode {
  privateKey: Uint8Array
  publicKey: Uint8Array
  chainCode: Uint8Array
  depth: number
  index: number
  parentFingerprint: Uint8Array
}

interface QuantumHDNode extends HDNode {
  quantumSeed: Uint8Array
  quantumKeyPair: QuantumKeyPair
}

// Extended node type that includes skip tracking for invalid key handling
interface QuantumHDNodeWithSkips extends QuantumHDNode {
  actualIndex?: number
  skipCount?: number
  effectivePath?: string // Track the effective path including skips
}

export interface AddressInfo {
  address: string
  path: string
  publicKey: Uint8Array
  quantumPublicKey: Uint8Array
  quantumAddress: string
  isUsed: boolean
  isChange: boolean
  index: number
  balance?: number
}

export interface PQPHDWalletOptions {
  /**
   * Enable experimental RKS (Rerandomizable Key Scheme) for watch-only wallets
   * @warning Creates PUBLIC-ONLY keys that cannot sign transactions
   * @default false
   */
  enableExperimentalRKS?: boolean
  
  /**
   * Enable persistence of used addresses across sessions
   * @default true
   */
  enablePersistence?: boolean
  
  /**
   * Maximum number of addresses to cache in memory
   * @default 1000
   */
  maxCacheSize?: number
  
  /**
   * Whether to throw when marking address without derivation path
   * @default true
   */
  throwOnMissingPath?: boolean
  
  /**
   * Allow RKS in production environments (requires explicit consent)
   * @default false
   */
  allowRKSInProduction?: boolean
  
  /**
   * Network to use for address generation
   * @default 'mainnet'
   */
  network?: 'mainnet' | 'testnet' | 'regtest'
}

export class PQPHDWallet {
  private masterSeed: Uint8Array | null = null
  private masterNode: QuantumHDNode | null = null
  private usedAddresses = new Set<string>()
  private addressCache = new Map<string, AddressInfo>()
  private addressCacheLRU = new Set<string>() // Track LRU order
  private webCrypto = WebCrypto.getInstance()
  private readonly enableRKS: boolean
  private readonly enablePersistence: boolean
  private walletId: string | null = null
  private readonly maxCacheSize: number // Maximum addresses to cache
  private readonly throwOnMissingPath: boolean
  private readonly network: 'mainnet' | 'testnet' | 'regtest'
  
  // Standard BIP32 constants
  private readonly HARDENED_OFFSET = 0x80000000
  private readonly MAINNET_PRIVATE = new Uint8Array([0x04, 0x88, 0xAD, 0xE4])
  private readonly MAINNET_PUBLIC = new Uint8Array([0x04, 0x88, 0xB2, 0x1E])
  private readonly TESTNET_PRIVATE = new Uint8Array([0x04, 0x35, 0x83, 0x94])
  private readonly TESTNET_PUBLIC = new Uint8Array([0x04, 0x35, 0x87, 0xCF])
  
  constructor(options: PQPHDWalletOptions = {}) {
    this.enableRKS = options.enableExperimentalRKS ?? false
    this.enablePersistence = options.enablePersistence ?? true
    this.maxCacheSize = options.maxCacheSize ?? 1000 // Default to 1000, but configurable
    this.throwOnMissingPath = options.throwOnMissingPath ?? true // Default to strict mode
    this.network = options.network ?? 'mainnet'
    
    // Extra safety check for RKS in production
    if (this.enableRKS) {
      const isProduction = process.env.NODE_ENV === 'production'
      if (isProduction && !options.allowRKSInProduction) {
        throw new Error(
          'RKS is an experimental feature and is disabled in production by default. ' +
          'To enable it, explicitly set allowRKSInProduction: true in wallet options. ' +
          'WARNING: RKS creates watch-only addresses that cannot spend funds!'
        )
      }
      
      logger.warn('⚠️ EXPERIMENTAL RKS ENABLED: This feature is for watch-only wallets only!')
      logger.warn('⚠️ DO NOT send funds to RKS-derived addresses without understanding the risks!')
      logger.warn('⚠️ RKS addresses are PUBLIC-ONLY and cannot sign transactions!')
    }
  }

  /**
   * Generate new mnemonic seed phrase
   */
  generateMnemonic(strength: 128 | 256 = 256): string {
    // Validate strength parameter
    if (strength !== 128 && strength !== 256) {
      throw new Error('Invalid mnemonic strength. Must be 128 or 256 bits.')
    }
    return generateMnemonic(wordlist, strength)
  }

  /**
   * Validate mnemonic
   */
  validateMnemonic(mnemonic: string): boolean {
    return validateMnemonic(mnemonic, wordlist)
  }

  /**
   * Initialize wallet from mnemonic
   */
  async initFromMnemonic(mnemonic: string, passphrase: string = ''): Promise<void> {
    if (!this.validateMnemonic(mnemonic)) {
      throw new Error('Invalid mnemonic phrase')
    }

    // Generate seed from mnemonic (BIP39)
    this.masterSeed = mnemonicToSeedSync(mnemonic, passphrase)
    
    // Derive master node
    this.masterNode = await this.generateMasterNode(this.masterSeed)
    
    // Clear caches
    this.addressCache.clear()
    this.addressCacheLRU.clear()
    
    // Generate wallet ID from master xpub hash for persistence
    if (this.enablePersistence) {
      const xpub = this.getMasterXPub()
      this.walletId = this.bytesToHex(sha256(new TextEncoder().encode(xpub))).slice(0, 16)
      
      // Load persisted used addresses
      if (typeof window !== 'undefined') {
        const store = useWalletAddresses.getState()
        const persistedAddresses = store.getUsedAddresses(this.walletId)
        this.usedAddresses = persistedAddresses
      }
    }
  }

  /**
   * Generate master node from seed
   */
  private async generateMasterNode(seed: Uint8Array): Promise<QuantumHDNode> {
    // Standard BIP32 master key generation
    const hmacSeed = hmac(sha512Hash, 'Bitcoin seed', seed)
    const masterPrivateKey = hmacSeed.slice(0, 32)
    const masterChainCode = hmacSeed.slice(32)

    // Check validity
    const n = BigInt('0xfffffffffffffffffffffffffffffffebaaedce6af48a03bbfd25e8cd0364141')
    if (BigInt('0x' + this.bytesToHex(masterPrivateKey)) >= n) {
      throw new Error('Invalid master key')
    }

    const masterPublicKey = secp256k1.getPublicKey(masterPrivateKey, true)

    // Derive quantum seed deterministically
    const quantumSeed = hmac(sha512Hash, 'Quantum seed', seed).slice(0, 32)
    const quantumKeyPair = await QuantumKeyManager.generateKeyPairFromSeed(quantumSeed)

    return {
      privateKey: masterPrivateKey,
      publicKey: masterPublicKey,
      chainCode: masterChainCode,
      depth: 0,
      index: 0,
      parentFingerprint: new Uint8Array(4),
      quantumSeed,
      quantumKeyPair
    }
  }

  /**
   * Derive child node (BIP32) with safe recursion for invalid keys
   * Enhanced with RKS support for non-hardened quantum derivation
   * 
   * @param parent - Parent node to derive from
   * @param index - Child index (>= 0x80000000 for hardened)
   * @param skipCount - Internal counter for invalid key skipping
   * @returns Child node with quantum keys
   * 
   * @warning RKS (Rerandomizable Key Scheme) Support:
   * - Only enabled for non-hardened paths when enableExperimentalRKS is true
   * - Creates PUBLIC-ONLY quantum keys that CANNOT sign transactions
   * - Addresses derived via RKS are watch-only and may be UNSPENDABLE
   * - Test thoroughly before using in production
   * - Requires explicit allowRKSInProduction flag in production environments
   * 
   * @throws If skip limit is reached (extremely rare ~1/2^128 probability)
   * @throws If RKS derivation returns invalid public key
   */
  private async deriveChild(parent: QuantumHDNode, index: number, skipCount: number = 0): Promise<QuantumHDNodeWithSkips> {
    const MAX_SKIP_ATTEMPTS = 5 // Reduced from 10 for better safety
    const baseIndex = index & 0x7FFFFFFF // Remove hardened bit for tracking
    const isHardened = index >= this.HARDENED_OFFSET
    
    // If we've skipped too many times, throw to prevent infinite loops
    if (skipCount >= MAX_SKIP_ATTEMPTS) {
      logger.error(`Key derivation skip limit reached at index ${index} after ${skipCount} attempts`)
      throw new Error(`Unable to derive valid key after ${MAX_SKIP_ATTEMPTS} attempts. This is extremely rare - consider using a different seed.`)
    }
    
    // Prepare data for HMAC
    const data = new Uint8Array(37)
    if (isHardened) {
      // Hardened: 0x00 || private key || index
      data[0] = 0x00
      data.set(parent.privateKey, 1)
    } else {
      // Non-hardened: public key || index
      data.set(parent.publicKey, 0)
    }
    
    // Set index (big-endian)
    data[33] = (index >>> 24) & 0xff
    data[34] = (index >>> 16) & 0xff
    data[35] = (index >>> 8) & 0xff
    data[36] = index & 0xff

    // Derive using HMAC-SHA512
    const hmacResult = hmac(sha512Hash, parent.chainCode, data)
    const childPrivateKeyBytes = hmacResult.slice(0, 32)
    const childChainCode = hmacResult.slice(32)

    // BIP32 validation: Check if IL >= n (secp256k1 order)
    const n = BigInt('0xfffffffffffffffffffffffffffffffebaaedce6af48a03bbfd25e8cd0364141')
    const IL = BigInt('0x' + this.bytesToHex(childPrivateKeyBytes))
    
    if (IL >= n) {
      // Per BIP32: if IL >= n, the resulting key is invalid
      // This is extremely rare (~1/2^128 probability)
      logger.warn(`Invalid child key at index ${index} (IL >= n). Skipping to next index.`)
      // Try next index with same hardened status
      const nextIndex = isHardened ? (baseIndex + skipCount + 1) | this.HARDENED_OFFSET : baseIndex + skipCount + 1
      // IMPORTANT: When skipping, the RKS derivation will use the new index to stay in sync
      return this.deriveChild(parent, nextIndex, skipCount + 1)
    }

    // Calculate child private key
    const parentKey = BigInt('0x' + this.bytesToHex(parent.privateKey))
    const childKeyNum = (IL + parentKey) % n
    
    if (childKeyNum === 0n) {
      // Per BIP32: if ki == 0, the resulting key is invalid
      // This is extremely rare (~1/2^256 probability)
      logger.warn(`Invalid child key at index ${index} (ki == 0). Skipping to next index.`)
      // Try next index with same hardened status
      const nextIndex = isHardened ? (baseIndex + skipCount + 1) | this.HARDENED_OFFSET : baseIndex + skipCount + 1
      // IMPORTANT: When skipping, the RKS derivation will use the new index to stay in sync
      return this.deriveChild(parent, nextIndex, skipCount + 1)
    }

    const childPrivateKey = this.bigIntToBytes(childKeyNum, 32)
    const childPublicKey = secp256k1.getPublicKey(childPrivateKey, true)

    // Derive quantum child
    let childQuantumSeed: Uint8Array
    let childQuantumKeyPair: QuantumKeyPair
    
    if (!isHardened && this.enableRKS && DilithiumRKSWrapper.isAvailable()) {
      // Try RKS for non-hardened derivation (only if explicitly enabled)
      logger.warn('⚠️ EXPERIMENTAL: Using Dilithium RKS for non-hardened derivation')
      logger.warn('⚠️ CRITICAL WARNING: RKS derivation creates PUBLIC-ONLY keys!')
      logger.warn('⚠️ Funds sent to these addresses may be UNSPENDABLE without the original private key!')
      
      try {
        const rks = getDilithiumRKS()
        const result = await rks.deriveChildPublicKey(
          parent.quantumKeyPair.publicKey,
          parent.chainCode,
          index
        )
        
        // Validate the derived public key
        if (!result.publicKey || result.publicKey.length === 0) {
          throw new Error('RKS derivation returned invalid public key')
        }
        
        // Validate Dilithium public key format (expected length for ML-DSA-65)
        const DILITHIUM3_PUBKEY_LENGTH = 1952 // Standard length for Dilithium3/ML-DSA-65
        if (result.publicKey.length !== DILITHIUM3_PUBKEY_LENGTH) {
          logger.warn(`RKS derived public key has unexpected length: ${result.publicKey.length} (expected ${DILITHIUM3_PUBKEY_LENGTH})`)
        }
        
        // CRITICAL: RKS creates public-only keys - no private key available!
        // This is intended for watch-only wallets but dangerous for spending wallets
        childQuantumSeed = new Uint8Array(32) // Zero seed - NO PRIVATE KEY
        
        // Create a minimal quantum key pair for RKS-derived keys
        // Compute the real quantum address from the public key
        const publicKeyHash = sha256(result.publicKey)
        const quantumAddress = await QuantumKeyManager.deriveQuantumAddress(
          result.publicKey, 
          parent.quantumKeyPair.network
        )
        
        childQuantumKeyPair = {
          publicKey: result.publicKey,
          privateKey: new Uint8Array(0), // EMPTY - Cannot sign with this!
          publicKeyHash,
          address: quantumAddress, // Real watch-only address derived from public key
          network: parent.quantumKeyPair.network,
          // Add metadata to track this is public-only
          isPublicOnly: true
        } as QuantumKeyPair & { isPublicOnly: boolean }
      } catch (error) {
        logger.error('RKS derivation failed, falling back to hardened:', error)
        // Fall back to HMAC-based derivation using SHA512 for consistency with BIP32
        childQuantumSeed = hmac(sha512Hash, parent.quantumSeed, data).slice(0, 32)
        childQuantumKeyPair = await QuantumKeyManager.generateKeyPairFromSeed(childQuantumSeed)
      }
    } else {
      // Standard derivation (hardened or RKS not available)
      // Use HMAC-SHA512 for consistency with classical BIP32 derivation
      childQuantumSeed = hmac(sha512Hash, parent.quantumSeed, data).slice(0, 32)
      childQuantumKeyPair = await QuantumKeyManager.generateKeyPairFromSeed(childQuantumSeed)
    }

    // Parent fingerprint
    const parentFingerprint = ripemd160(sha256(parent.publicKey)).slice(0, 4)

    const result: QuantumHDNodeWithSkips = {
      privateKey: childPrivateKey,
      publicKey: childPublicKey,
      chainCode: childChainCode,
      depth: parent.depth + 1,
      index,
      parentFingerprint,
      quantumSeed: childQuantumSeed,
      quantumKeyPair: childQuantumKeyPair,
      actualIndex: skipCount > 0 ? index : undefined,
      skipCount: skipCount > 0 ? skipCount : undefined
    }
    
    return result
  }

  /**
   * Derive path (e.g., "m/3077'/0'/0'/0/0")
   * Returns extended node with skip information if invalid keys were encountered
   */
  private async derivePath(path: string): Promise<QuantumHDNodeWithSkips> {
    if (!this.masterNode) {
      throw new Error('Wallet not initialized')
    }

    const parts = path.split('/')
    if (parts[0] !== 'm') {
      throw new Error('Path must start with "m"')
    }

    // Validate path depth (BIP32 specifies max depth of 255)
    // parts.length - 1 because first part is 'm'
    if (parts.length - 1 > 255) {
      throw new Error('Path too deep (max 255 levels)')
    }

    let node: QuantumHDNodeWithSkips = this.masterNode
    let effectivePath = 'm'
    
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i]
      if (!part) continue // Skip empty parts
      
      let index: number
      const isHardened = part.endsWith("'")
      if (isHardened) {
        // Hardened derivation
        const num = parseInt(part.slice(0, -1), 10)
        if (isNaN(num) || num < 0 || num >= this.HARDENED_OFFSET) {
          throw new Error(`Invalid hardened index at position ${i}: ${part}`)
        }
        index = num + this.HARDENED_OFFSET
      } else {
        // Non-hardened derivation
        index = parseInt(part, 10)
        if (isNaN(index) || index < 0 || index >= this.HARDENED_OFFSET) {
          throw new Error(`Invalid index at position ${i}: ${part}`)
        }
      }
      
      const childNode = await this.deriveChild(node, index)
      
      // Update effective path if skips occurred
      if (childNode.skipCount && childNode.skipCount > 0) {
        const baseIndex = index & 0x7FFFFFFF // Remove hardened bit
        const effectiveIndex = childNode.actualIndex! & 0x7FFFFFFF
        effectivePath += '/' + effectiveIndex + (isHardened ? "'+skip" + childNode.skipCount : "+skip" + childNode.skipCount)
      } else {
        effectivePath += '/' + part
      }
      
      node = childNode
    }
    
    // Propagate the effective path
    node.effectivePath = effectivePath
    return node
  }

  /**
   * Get address at specific path
   */
  async getAddress(
    account: number = 0,
    change: 0 | 1 = 0,
    index: number = 0,
    testnet: boolean = false
  ): Promise<AddressInfo> {
    // Validate inputs
    if (account < 0 || index < 0) {
      throw new Error('Account and index must be non-negative')
    }
    
    // Use network from options if testnet param not explicitly provided
    const isTestnet = testnet || this.network === 'testnet' || this.network === 'regtest'
    const coinType = isTestnet ? TESTNET_COIN_TYPE : BITCOIN_COIN_TYPE
    const requestedPath = `m/${QUANTUM_PURPOSE}'/${coinType}'/${account}'/${change}/${index}`
    
    // Derive with potential skips
    const node = await this.derivePath(requestedPath)
    
    // Use effective path if skips occurred, otherwise use requested path
    const effectivePath = node.effectivePath || requestedPath
    
    // Check cache using effective path
    const cacheKey = `${effectivePath}:${isTestnet}`
    if (this.addressCache.has(cacheKey)) {
      // Update LRU order - move to end (most recently used)
      this.updateLRU(cacheKey)
      return this.addressCache.get(cacheKey)!
    }
    
    // Create standard Bitcoin address (P2WPKH)
    const address = this.createP2WPKHAddress(node.publicKey, isTestnet)
    
    const addressInfo: AddressInfo = {
      address,
      path: effectivePath, // Store effective path for consistency
      publicKey: node.publicKey,
      quantumPublicKey: node.quantumKeyPair.publicKey,
      quantumAddress: node.quantumKeyPair.address,
      isUsed: this.usedAddresses.has(address),
      isChange: change === 1,
      index: node.actualIndex !== undefined ? (node.actualIndex & 0x7FFFFFFF) : index // Use actual index if skipped
    }

    // Add to cache with LRU eviction if needed
    if (this.addressCache.size >= this.maxCacheSize) {
      // Remove least recently used entry
      const lruKey = this.addressCacheLRU.values().next().value
      if (lruKey) {
        this.addressCache.delete(lruKey)
        this.addressCacheLRU.delete(lruKey)
      }
    }
    
    this.addressCache.set(cacheKey, addressInfo)
    this.updateLRU(cacheKey)
    return addressInfo
  }

  /**
   * Update LRU order for cache key
   */
  private updateLRU(key: string): void {
    // Remove from current position if exists
    if (this.addressCacheLRU.has(key)) {
      this.addressCacheLRU.delete(key)
    }
    // Add to end (most recently used)
    this.addressCacheLRU.add(key)
  }

  /**
   * Get next unused address
   */
  async getNextAddress(
    account: number = 0,
    isChange: boolean = false,
    testnet: boolean = false
  ): Promise<AddressInfo> {
    const change = isChange ? 1 : 0
    let index = 0
    
    while (true) {
      const addr = await this.getAddress(account, change, index, testnet)
      if (!addr.isUsed) {
        return addr
      }
      index++
      
      // Safety limit
      if (index > 1000) {
        throw new Error('No unused addresses found in first 1000 indices')
      }
    }
  }

  /**
   * Get multiple addresses (for display/scanning)
   */
  async getAddresses(
    account: number = 0,
    count: number = 20,
    testnet: boolean = false
  ): Promise<{
    external: AddressInfo[]
    change: AddressInfo[]
  }> {
    const external: AddressInfo[] = []
    const change: AddressInfo[] = []

    for (let i = 0; i < count; i++) {
      external.push(await this.getAddress(account, 0, i, testnet))
      change.push(await this.getAddress(account, 1, i, testnet))
    }

    return { external, change }
  }

  /**
   * Create P2WPKH (native SegWit) address
   */
  private createP2WPKHAddress(publicKey: Uint8Array, testnet: boolean): string {
    const hash160 = ripemd160(sha256(publicKey))
    const hrp = testnet ? 'tb' : 'bc'
    
    // Convert hash160 to 5-bit words for bech32
    // Witness version (0) is prepended separately
    const words = bech32.toWords(Buffer.from(hash160))
    return bech32.encode(hrp, [0, ...words]) // 0 is witness version
  }

  /**
   * Mark address as used (for gap limit)
   */
  markAddressUsed(address: string, path?: string): void {
    // Update cache and persist if enabled
    let found = false
    let derivedPath = path
    
    for (const [key, info] of this.addressCache.entries()) {
      if (info.address === address) {
        info.isUsed = true
        found = true
        derivedPath = info.path // Use path from cache if available
        
        // Persist to store if enabled
        if (this.enablePersistence && this.walletId && typeof window !== 'undefined') {
          const store = useWalletAddresses.getState()
          store.markAddressUsed(this.walletId, address, info.path)
        }
        break
      }
    }
    
    // If not in cache, handle persistence
    if (!found) {
      if (derivedPath) {
        // We have a path, persist it
        if (this.enablePersistence && this.walletId && typeof window !== 'undefined') {
          const store = useWalletAddresses.getState()
          store.markAddressUsed(this.walletId, address, derivedPath)
        }
      } else {
        // No path available and not in cache - this is problematic for recovery
        const errorMsg = `Cannot mark address ${address} as used without derivation path. ` +
                        `This address was not derived through this wallet and cannot be persisted properly. ` +
                        `Consider deriving the address first using getAddress() or providing the path.`
        
        if (this.throwOnMissingPath) {
          logger.error(errorMsg)
          throw new Error(errorMsg)
        } else {
          // Log warning but continue - allow placeholder persistence
          logger.warn(errorMsg)
          logger.warn(`Address ${address} marked as used but may not persist correctly without path`)
        }
      }
    }
    
    // Only add to used addresses after validation passes
    this.usedAddresses.add(address)
  }

  /**
   * Get master extended public key (xpub)
   */
  getMasterXPub(testnet: boolean = false): string {
    if (!this.masterNode) {
      throw new Error('Wallet not initialized')
    }

    // BIP32 serialization format
    const data = new Uint8Array(78)
    const versionBytes = testnet ? this.TESTNET_PUBLIC : this.MAINNET_PUBLIC
    data.set(versionBytes, 0) // Version bytes
    data[4] = this.masterNode.depth
    data.set(this.masterNode.parentFingerprint, 5)
    
    // Child number (big-endian)
    data[9] = (this.masterNode.index >>> 24) & 0xff
    data[10] = (this.masterNode.index >>> 16) & 0xff
    data[11] = (this.masterNode.index >>> 8) & 0xff
    data[12] = this.masterNode.index & 0xff
    
    data.set(this.masterNode.chainCode, 13)
    data.set(this.masterNode.publicKey, 45)

    // Base58Check encode
    return this.base58CheckEncode(data)
  }

  /**
   * Get account extended public key (classical)
   */
  async getAccountXPub(account: number = 0, testnet: boolean = false): Promise<string> {
    const coinType = testnet ? TESTNET_COIN_TYPE : BITCOIN_COIN_TYPE
    const path = `m/${QUANTUM_PURPOSE}'/${coinType}'/${account}'`
    const node = await this.derivePath(path)

    const data = new Uint8Array(78)
    // Use appropriate version bytes for mainnet/testnet
    const versionBytes = testnet ? this.TESTNET_PUBLIC : this.MAINNET_PUBLIC
    data.set(versionBytes, 0)
    data[4] = node.depth
    data.set(node.parentFingerprint, 5)
    
    data[9] = (node.index >>> 24) & 0xff
    data[10] = (node.index >>> 16) & 0xff
    data[11] = (node.index >>> 8) & 0xff
    data[12] = node.index & 0xff
    
    data.set(node.chainCode, 13)
    data.set(node.publicKey, 45)

    return this.base58CheckEncode(data)
  }

  /**
   * Get quantum extended public key (dpub) - EXPERIMENTAL
   * This enables watch-only wallets with Dilithium RKS
   * 
   * WARNING: The dpub format is non-standard and specific to this implementation.
   * Other wallets will not recognize this format. The testnet version uses tpub
   * version bytes which may cause confusion - there is no standard "dtub" format.
   * Only export/share dpubs if the receiving software explicitly supports this format.
   * 
   * @param account - Account number (default 0)
   * @param testnet - Whether to generate testnet dpub (default false)
   * @returns Quantum extended public key in custom dpub format, or null if RKS unavailable
   */
  async getQuantumDPub(account: number = 0, testnet: boolean = false): Promise<string | null> {
    if (!DilithiumRKSWrapper.isAvailable()) {
      logger.warn('Dilithium RKS not available - cannot generate dpub')
      return null
    }

    const coinType = testnet ? TESTNET_COIN_TYPE : BITCOIN_COIN_TYPE
    const path = `m/${QUANTUM_PURPOSE}'/${coinType}'/${account}'`
    const node = await this.derivePath(path)

    // Serialize quantum extended public key
    // Format: version(4) + depth(1) + fingerprint(4) + index(4) + chaincode(32) + pubkey(variable length)
    // NOTE: "dpub" is a custom format for Dilithium extended public keys, not a standard
    // Testnet uses tpub version bytes for consistency, but this is non-standard
    const DPUB_VERSION = testnet 
      ? new Uint8Array([0x04, 0x35, 0x87, 0xCF]) // Non-standard: uses tpub version for testnet dpub
      : new Uint8Array([0x04, 0xB2, 0x47, 0x46]) // Custom "dpub" version for mainnet
    const totalSize = 4 + 1 + 4 + 4 + 32 + node.quantumKeyPair.publicKey.length
    const data = new Uint8Array(totalSize)
    
    let offset = 0
    data.set(DPUB_VERSION, offset); offset += 4
    data[offset] = node.depth; offset += 1
    data.set(node.parentFingerprint, offset); offset += 4
    
    data[offset] = (node.index >>> 24) & 0xff; offset += 1
    data[offset] = (node.index >>> 16) & 0xff; offset += 1
    data[offset] = (node.index >>> 8) & 0xff; offset += 1
    data[offset] = node.index & 0xff; offset += 1
    
    data.set(node.chainCode, offset); offset += 32
    data.set(node.quantumKeyPair.publicKey, offset)

    return this.base58CheckEncode(data)
  }

  /**
   * Get private key for specific address
   */
  async getPrivateKey(path: string): Promise<Uint8Array> {
    const node = await this.derivePath(path)
    return node.privateKey
  }

  /**
   * Get quantum seed for specific address
   */
  async getQuantumSeed(path: string): Promise<Uint8Array> {
    const node = await this.derivePath(path)
    
    // Check if this is a public-only key from RKS derivation
    if ('isPublicOnly' in node.quantumKeyPair && node.quantumKeyPair.isPublicOnly) {
      throw new Error('Cannot access quantum seed: This is a public-only address (RKS-derived) with no private key.')
    }
    
    return node.quantumSeed
  }

  /**
   * Get full key material for PQP transaction
   */
  async getKeysForAddress(address: string): Promise<{
    classical: { privateKey: Uint8Array; publicKey: Uint8Array }
    quantum: { seed: Uint8Array; keyPair: QuantumKeyPair }
    path: string
  } | null> {
    // Find address in cache
    for (const [key, info] of this.addressCache.entries()) {
      if (info.address === address) {
        const node = await this.derivePath(info.path)
        
        // Check if this is a public-only key from RKS derivation
        if ('isPublicOnly' in node.quantumKeyPair && node.quantumKeyPair.isPublicOnly) {
          throw new Error('Cannot access private keys: This is a public-only address (RKS-derived) with no private key.')
        }
        
        return {
          classical: {
            privateKey: node.privateKey,
            publicKey: node.publicKey
          },
          quantum: {
            seed: node.quantumSeed,
            keyPair: node.quantumKeyPair
          },
          path: info.path
        }
      }
    }
    return null
  }

  /**
   * Get a hash of the extended public key for wallet identification
   */
  async getXpubHash(accountIndex: number = 0, testnet: boolean = false): Promise<string> {
    // Derive the account node
    const accountPath = `m/3077'/${testnet ? 1 : 0}'/${accountIndex}'`
    const node = await this.derivePath(accountPath)
    
    // Create a unique identifier from the account public key
    // We'll use the SHA-256 hash of the classical public key + quantum public key hash
    const encoder = new TextEncoder()
    const combinedData = new Uint8Array([
      ...node.publicKey,
      ...sha256(node.quantumKeyPair.publicKey).slice(0, 32)
    ])
    
    // Use Web Crypto if available, fallback to @noble/hashes
    let hashArray: number[]
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const hashBuffer = await crypto.subtle.digest('SHA-256', combinedData)
      hashArray = Array.from(new Uint8Array(hashBuffer))
    } else {
      // Fallback for non-browser environments
      const hash = sha256(combinedData)
      hashArray = Array.from(hash)
    }
    
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16) // First 16 chars of hash
  }
  
  /**
   * Create QD3 data for an address
   * @param address - The Bitcoin address
   * @param pathOrMessage - Either derivation path (string) or message to sign (string) 
   * @returns QD3 data object or null if address not found
   */
  async createQD3ForAddress(address: string, pathOrMessage?: string): Promise<{ hex: string } | null> {
    const keys = await this.getKeysForAddress(address)
    if (!keys) return null

    // Check if this is a public-only key (from RKS derivation)
    if (!keys.quantum.keyPair.privateKey || keys.quantum.keyPair.privateKey.length === 0) {
      logger.error('Cannot create QD3 signature: This is a public-only address (no private key)')
      throw new Error('Cannot sign with public-only address. This address was derived using RKS and has no private key.')
    }

    // Default message if not provided
    const message = pathOrMessage || address
    const messageBytes = new TextEncoder().encode(message)
    const signature = await QuantumKeyManager.signMessage(keys.quantum.keyPair.privateKey, messageBytes)
    const qd3Data = await QuantumKeyManager.createQD3Data(keys.quantum.keyPair.publicKey, signature)
    
    return qd3Data
  }

  /**
   * Clear sensitive data
   */
  clear(): void {
    if (this.masterSeed) {
      this.masterSeed.fill(0)
      this.masterSeed = null
    }
    if (this.masterNode) {
      // Clear all sensitive key material
      this.masterNode.privateKey.fill(0)
      this.masterNode.quantumSeed.fill(0)
      
      // Clear quantum private key if it exists
      if (this.masterNode.quantumKeyPair.privateKey && 
          this.masterNode.quantumKeyPair.privateKey.length > 0) {
        this.masterNode.quantumKeyPair.privateKey.fill(0)
      }
      
      // Clear chain code for extra security
      this.masterNode.chainCode.fill(0)
      
      this.masterNode = null
    }
    
    // Clear address cache which might contain derived keys
    // Note: We don't store child private keys in cache, only public info
    this.addressCache.clear()
    this.addressCacheLRU.clear()
    this.usedAddresses.clear()
    this.walletId = null
  }

  /**
   * Utility functions
   */
  private bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  }

  private bigIntToBytes(num: bigint, length: number): Uint8Array {
    const hex = num.toString(16).padStart(length * 2, '0')
    const bytes = new Uint8Array(length)
    for (let i = 0; i < length; i++) {
      bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
    }
    return bytes
  }

  private base58CheckEncode(data: Uint8Array): string {
    // Use audited @scure/base library with double SHA256 as per Bitcoin standards
    const doubleHash = (data: Uint8Array) => sha256(sha256(data))
    return base58check(doubleHash).encode(data)
  }

}

// Factory function to create new instances
export function createPQPHDWallet(options?: PQPHDWalletOptions): PQPHDWallet {
  return new PQPHDWallet(options)
}

// Default singleton for backward compatibility
// WARNING: In multi-user environments, use createPQPHDWallet() instead
export const pqpHDWallet = createPQPHDWallet()